import React, { useState, useMemo } from 'react';
import {
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Download,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  PlayArrow,
  Done,
  ThumbUp,
  ThumbDown,
} from '@mui/icons-material';
import { formatCurrency } from './KPISummaryCards';

/**
 * Status chip with colour coding.
 */
const StatusChip = ({ status }) => {
  const config = {
    APPLIED:       { label: 'Applied',      color: 'info',    icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    PENDING:       { label: 'Pending',      color: 'warning',  icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    UNDER_REVIEW:  { label: 'Under Review', color: 'info',     icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    APPROVED:      { label: 'Approved',     color: 'success',  icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    DISBURSED:     { label: 'Disbursed',    color: 'primary',  icon: <PlayArrow sx={{ fontSize: 14 }} /> },
    ACTIVE:        { label: 'Active',       color: 'success',  icon: <PlayArrow sx={{ fontSize: 14 }} /> },
    SETTLED:       { label: 'Settled',      color: 'default',  icon: <Done sx={{ fontSize: 14 }} /> },
    REJECTED:      { label: 'Rejected',     color: 'error',    icon: <Cancel sx={{ fontSize: 14 }} /> },
    DEFAULTED:     { label: 'Defaulted',    color: 'error',    icon: <Cancel sx={{ fontSize: 14 }} /> },
    REPAID:        { label: 'Repaid',       color: 'success',  icon: <Done sx={{ fontSize: 14 }} /> },
  };
  const c = config[(status || '').toUpperCase()] || { label: status || 'Unknown', color: 'default', icon: null };

  return (
    <Chip
      icon={c.icon}
      label={c.label}
      color={c.color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
    />
  );
};

/**
 * Helper: can this application be approved / rejected?
 */
const canActOn = (status) => {
  const s = (status || '').toLowerCase();
  return ['pending', 'under_review'].includes(s);
};

/**
 * ApplicationsTab
 *
 * Table of all credit applications with search, filter, approve/reject actions.
 */
const ApplicationsTab = ({ applications = [], loading = false, onApprove, onReject }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Approve dialog state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveForm, setApproveForm] = useState({
    sum_insured: '',
    credit_score: '',
    interest_rate: '12',
    repayment_type: 'bullet',
  });
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState('');

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState('');

  // Detail dialog state
  const [detailApp, setDetailApp] = useState(null);

  const statuses = ['ALL', 'PENDING', 'UNDER_REVIEW', 'APPROVED', 'DISBURSED', 'ACTIVE', 'SETTLED', 'REJECTED', 'DEFAULTED'];

  const filtered = useMemo(() => {
    return applications.filter((app) => {
      const matchesSearch =
        !search ||
        (app.farmer_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (app.id?.toString() || '').includes(search) ||
        (app.application_number || '').toLowerCase().includes(search.toLowerCase()) ||
        (app.pond_name || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'ALL' ||
        (app.status || '').toUpperCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [applications, search, statusFilter]);

  // ── Approve dialog ──
  const openApproveDialog = (app) => {
    setApproveTarget(app);
    setApproveForm({
      sum_insured: String(app.requested_amount || 100000),
      credit_score: String(app.risk_score || ''),
      interest_rate: '12',
      repayment_type: 'bullet',
    });
    setApproveError('');
    setApproveDialogOpen(true);
  };

  const handleApproveSubmit = async () => {
    if (!approveTarget || !onApprove) return;
    const sumInsured = parseFloat(approveForm.sum_insured);
    if (!sumInsured || sumInsured <= 0) {
      setApproveError('Sum insured must be a positive number');
      return;
    }
    setApproveLoading(true);
    setApproveError('');
    const payload = {
      sum_insured: sumInsured,
      credit_score: approveForm.credit_score ? parseFloat(approveForm.credit_score) : null,
      interest_rate: parseFloat(approveForm.interest_rate) || 12,
      repayment_type: approveForm.repayment_type || 'bullet',
    };
    const result = await onApprove(approveTarget.id, payload);
    setApproveLoading(false);
    if (result.success) {
      setApproveDialogOpen(false);
      setApproveTarget(null);
    } else {
      setApproveError(result.error || 'Approval failed');
    }
  };

  // ── Reject dialog ──
  const openRejectDialog = (app) => {
    setRejectTarget(app);
    setRejectReason('');
    setRejectError('');
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectTarget || !onReject) return;
    if (rejectReason.trim().length < 5) {
      setRejectError('Rejection reason must be at least 5 characters');
      return;
    }
    setRejectLoading(true);
    setRejectError('');
    const result = await onReject(rejectTarget.id, rejectReason.trim());
    setRejectLoading(false);
    if (result.success) {
      setRejectDialogOpen(false);
      setRejectTarget(null);
    } else {
      setRejectError(result.error || 'Rejection failed');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-10 bg-gray-200 rounded w-full" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 bg-gray-100 rounded w-full" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <TextField
          placeholder="Search by name, ID, pond, or app number..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: '#9ca3af' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />

        {/* Status filters */}
        <div className="flex items-center gap-1 flex-wrap">
          <FilterList sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />
          {statuses.map((s) => (
            <Chip
              key={s}
              label={s === 'ALL' ? 'All' : s.replace('_', ' ').charAt(0) + s.replace('_', ' ').slice(1).toLowerCase()}
              variant={statusFilter === s ? 'filled' : 'outlined'}
              color={statusFilter === s ? 'primary' : 'default'}
              size="small"
              onClick={() => setStatusFilter(s)}
              sx={{ fontWeight: 500, fontSize: '0.65rem', cursor: 'pointer' }}
            />
          ))}
        </div>

        <div className="ml-auto">
          <Tooltip title="Export CSV">
            <IconButton size="small">
              <Download sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs text-gray-400 mb-3">
        Showing {filtered.length} of {applications.length} applications
      </p>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Farmer</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Pond</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Risk Score</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Applied On</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-gray-400 text-sm">
                  {applications.length === 0
                    ? 'No credit applications found'
                    : 'No applications match your filters'}
                </td>
              </tr>
            ) : (
              filtered.map((app) => (
                <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">#{app.id}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{app.farmer_name || 'N/A'}</p>
                    <p className="text-xs text-gray-400">{app.farmer_phone || ''}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{app.pond_name || '—'}</td>
                  <td className="px-4 py-3 font-semibold text-gray-800">
                    {formatCurrency(app.requested_amount || app.approved_amount || 0)}
                  </td>
                  <td className="px-4 py-3">
                    {app.risk_score != null ? (
                      <span
                        className={`text-xs font-bold px-2 py-0.5 rounded ${
                          app.risk_score >= 750
                            ? 'bg-green-50 text-green-700'
                            : app.risk_score >= 550
                            ? 'bg-yellow-50 text-yellow-700'
                            : 'bg-red-50 text-red-700'
                        }`}
                      >
                        {app.risk_score}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusChip status={app.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {(app.applied_date || app.created_at)
                      ? new Date(app.applied_date || app.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => setDetailApp(app)}>
                          <Visibility sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {canActOn(app.status) && onApprove && (
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            onClick={() => openApproveDialog(app)}
                            sx={{ color: '#16a34a' }}
                          >
                            <ThumbUp sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canActOn(app.status) && onReject && (
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            onClick={() => openRejectDialog(app)}
                            sx={{ color: '#dc2626' }}
                          >
                            <ThumbDown sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Approve Dialog ── */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !approveLoading && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Approve Credit Application #{approveTarget?.id}
        </DialogTitle>
        <DialogContent dividers>
          {approveError && (
            <Alert severity="error" sx={{ mb: 2 }}>{approveError}</Alert>
          )}
          <div className="space-y-4 mt-1">
            <p className="text-sm text-gray-600">
              <strong>Farmer:</strong> {approveTarget?.farmer_name || 'N/A'}
              &nbsp;|&nbsp;
              <strong>Requested:</strong> {formatCurrency(approveTarget?.requested_amount || 0)}
            </p>
            <TextField
              label="Sum Insured (₹)"
              type="number"
              fullWidth
              size="small"
              value={approveForm.sum_insured}
              onChange={(e) => setApproveForm((f) => ({ ...f, sum_insured: e.target.value }))}
              helperText="From farmer's active insurance policy"
            />
            <TextField
              label="Credit Score (0–900, optional)"
              type="number"
              fullWidth
              size="small"
              value={approveForm.credit_score}
              onChange={(e) => setApproveForm((f) => ({ ...f, credit_score: e.target.value }))}
            />
            <TextField
              label="Interest Rate (%)"
              type="number"
              fullWidth
              size="small"
              value={approveForm.interest_rate}
              onChange={(e) => setApproveForm((f) => ({ ...f, interest_rate: e.target.value }))}
            />
            <TextField
              label="Repayment Type"
              select
              fullWidth
              size="small"
              value={approveForm.repayment_type}
              onChange={(e) => setApproveForm((f) => ({ ...f, repayment_type: e.target.value }))}
              SelectProps={{ native: true }}
            >
              <option value="bullet">Bullet (Single at harvest)</option>
              <option value="emi">EMI (Monthly)</option>
            </TextField>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setApproveDialogOpen(false)}
            disabled={approveLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveSubmit}
            disabled={approveLoading}
            startIcon={approveLoading ? <CircularProgress size={16} /> : <ThumbUp sx={{ fontSize: 16 }} />}
          >
            {approveLoading ? 'Approving…' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject Dialog ── */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !rejectLoading && setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>
          Reject Credit Application #{rejectTarget?.id}
        </DialogTitle>
        <DialogContent dividers>
          {rejectError && (
            <Alert severity="error" sx={{ mb: 2 }}>{rejectError}</Alert>
          )}
          <div className="space-y-3 mt-1">
            <p className="text-sm text-gray-600">
              <strong>Farmer:</strong> {rejectTarget?.farmer_name || 'N/A'}
              &nbsp;|&nbsp;
              <strong>Requested:</strong> {formatCurrency(rejectTarget?.requested_amount || 0)}
            </p>
            <TextField
              label="Rejection Reason"
              multiline
              rows={3}
              fullWidth
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Provide a clear reason for rejection (min 5 characters)"
              helperText={`${rejectReason.length} / 5 minimum characters`}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            disabled={rejectLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={rejectLoading || rejectReason.trim().length < 5}
            startIcon={rejectLoading ? <CircularProgress size={16} /> : <ThumbDown sx={{ fontSize: 16 }} />}
          >
            {rejectLoading ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Detail Dialog ── */}
      <Dialog
        open={!!detailApp}
        onClose={() => setDetailApp(null)}
        maxWidth="sm"
        fullWidth
      >
        {detailApp && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              Application #{detailApp.id} — {detailApp.farmer_name}
            </DialogTitle>
            <DialogContent dividers>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400 text-xs">Application Number</span>
                  <p className="font-medium">{detailApp.application_number || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Status</span>
                  <p><StatusChip status={detailApp.status} /></p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Farmer</span>
                  <p className="font-medium">{detailApp.farmer_name}</p>
                  <p className="text-xs text-gray-400">{detailApp.farmer_phone || ''}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Pond</span>
                  <p className="font-medium">{detailApp.pond_name || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Requested Amount</span>
                  <p className="font-semibold">{formatCurrency(detailApp.requested_amount || 0)}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Approved Amount</span>
                  <p className="font-semibold">{detailApp.approved_amount ? formatCurrency(detailApp.approved_amount) : '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Risk Score</span>
                  <p className="font-medium">{detailApp.risk_score ?? '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Risk Level</span>
                  <p className="font-medium capitalize">{detailApp.risk_level || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Loan Type</span>
                  <p className="font-medium capitalize">{detailApp.loan_type || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Tenure</span>
                  <p className="font-medium">{detailApp.tenure_months ? `${detailApp.tenure_months} months` : '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Interest Rate</span>
                  <p className="font-medium">{detailApp.interest_rate ? `${detailApp.interest_rate}%` : '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Repayment Type</span>
                  <p className="font-medium capitalize">{detailApp.repayment_type || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Purpose</span>
                  <p className="font-medium">{detailApp.purpose || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Applied Date</span>
                  <p className="font-medium">
                    {(detailApp.applied_date || detailApp.created_at)
                      ? new Date(detailApp.applied_date || detailApp.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })
                      : '—'}
                  </p>
                </div>
                {detailApp.rejection_reason && (
                  <div className="col-span-2">
                    <span className="text-gray-400 text-xs">Rejection Reason</span>
                    <p className="font-medium text-red-600">{detailApp.rejection_reason}</p>
                  </div>
                )}
                {detailApp.disbursed_amount > 0 && (
                  <div>
                    <span className="text-gray-400 text-xs">Disbursed Amount</span>
                    <p className="font-semibold">{formatCurrency(detailApp.disbursed_amount)}</p>
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              {canActOn(detailApp.status) && onApprove && (
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  startIcon={<ThumbUp sx={{ fontSize: 14 }} />}
                  onClick={() => { setDetailApp(null); openApproveDialog(detailApp); }}
                >
                  Approve
                </Button>
              )}
              {canActOn(detailApp.status) && onReject && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<ThumbDown sx={{ fontSize: 14 }} />}
                  onClick={() => { setDetailApp(null); openRejectDialog(detailApp); }}
                >
                  Reject
                </Button>
              )}
              <Button onClick={() => setDetailApp(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ApplicationsTab;
